import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MsalService } from '@azure/msal-angular';
import { Subscription, catchError, map, of, switchMap, timer } from 'rxjs';

import { CompaniesService } from '../../../services/companies.service';
import { PresenceService, PresenceStatus, UserPresence } from '../../../core/presence/presence.service';
import { CompanyUserDTO } from '../../../models/company/company-user-dto.model';
import { UserStatus } from '../../../enums/user-status.enum';

interface PresenceRow {
  azureObjectId: string;
  name: string;
  email: string;
  initials: string;
  status: PresenceStatus;
  detail: string;
  isMe: boolean;
}

const STATUS_ORDER: Record<PresenceStatus, number> = { available: 0, away: 1, offline: 2 };
const REFRESH_MS = 30_000;

/**
 * The header's "who's in" list: every user of the open company, marked Active, Idle or Offline
 * from the automatic check-in (see PresenceService). Loads only while open, so a closed menu costs
 * nothing.
 */
@Component({
  selector: 'app-team-presence',
  imports: [CommonModule, MatIconModule, MatMenuModule, MatTooltipModule],
  templateUrl: './team-presence.component.html',
  styleUrls: ['./team-presence.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TeamPresenceComponent {
  private readonly presence = inject(PresenceService);
  private readonly companies = inject(CompaniesService);
  private readonly msal = inject(MsalService);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(false);
  readonly failed = signal(false);
  readonly filter = signal('');
  private readonly rows = signal<PresenceRow[]>([]);

  readonly visibleRows = computed(() => {
    const q = this.filter().trim().toLowerCase();
    return q
      ? this.rows().filter(r => r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q))
      : this.rows();
  });
  readonly groups = computed(() => {
    const rows = this.visibleRows();
    return ([
      { status: 'available', label: 'Active' },
      { status: 'away', label: 'Idle' },
      { status: 'offline', label: 'Offline' },
    ] as const)
      .map(g => ({ ...g, rows: rows.filter(r => r.status === g.status) }))
      .filter(g => g.rows.length > 0);
  });
  readonly activeCount = computed(() => this.rows().filter(r => r.status === 'available').length);

  private users: CompanyUserDTO[] = [];
  private refresh?: Subscription;

  constructor() {
    this.destroyRef.onDestroy(() => this.refresh?.unsubscribe());
  }

  /**
   * Warms the list when the pointer reaches the button (or it takes focus), so the menu usually
   * opens already filled. The first open used to wait on two calls in a row — the company's users,
   * then their presence — while the menu showed a spinner. At most once a minute, and never while
   * the menu is open (it refreshes itself then).
   */
  prefetch(): void {
    if (this.refresh || Date.now() - this.prefetchedAt < 60_000) return;
    this.prefetchedAt = Date.now();
    this.companies.getCompanyUsers().pipe(
      map(res => (res?.users ?? []).filter(u =>
        !!u.azureObjectId && u.status === UserStatus.Approved && u.isActive !== false)),
      switchMap(users => this.presence.getPresence(users.map(u => u.azureObjectId)).pipe(
        map(presence => ({ users, presence })))),
      catchError(() => of(null)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(result => {
      // The menu may have opened meanwhile and loaded its own answer; that one is at least as new.
      if (!result || this.refresh) return;
      this.users = result.users;
      this.rows.set(this.buildRows(result.presence));
    });
  }
  private prefetchedAt = 0;

  onOpened(): void {
    this.filter.set('');
    this.loading.set(this.rows().length === 0);
    this.failed.set(false);
    this.refresh?.unsubscribe();
    // The user list can change while the menu is closed (org switch, invite), so fetch it per open;
    // presence then refreshes on a timer until the menu closes.
    this.refresh = this.companies.getCompanyUsers().pipe(
      map(res => (res?.users ?? []).filter(u =>
        !!u.azureObjectId && u.status === UserStatus.Approved && u.isActive !== false)),
      catchError(() => of(null)),
      switchMap(users => {
        if (users === null) {
          this.failed.set(true);
          return of(null);
        }
        this.users = users;
        return timer(0, REFRESH_MS).pipe(
          switchMap(() => this.presence.getPresence(users.map(u => u.azureObjectId)).pipe(
            catchError(() => of(null)))),
        );
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(result => {
      this.loading.set(false);
      if (result === null) {
        this.failed.set(true);
        return;
      }
      this.failed.set(false);
      this.rows.set(this.buildRows(result));
    });
  }

  onClosed(): void {
    this.refresh?.unsubscribe();
    this.refresh = undefined;
  }

  onFilter(value: string): void {
    this.filter.set(value);
  }

  trackRow(_: number, row: PresenceRow): string {
    return row.azureObjectId;
  }

  private buildRows(presence: UserPresence[]): PresenceRow[] {
    const byId = new Map(presence.map(p => [p.azureObjectId, p]));
    const me = this.myObjectId();
    return this.users
      .map(u => {
        const p = byId.get(u.azureObjectId);
        const status: PresenceStatus = p?.status ?? 'offline';
        const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email;
        return {
          azureObjectId: u.azureObjectId,
          name,
          email: u.email ?? '',
          initials: this.initials(name),
          status,
          detail: this.detail(status, p),
          isMe: u.azureObjectId === me,
        };
      })
      .sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || a.name.localeCompare(b.name));
  }

  private detail(status: PresenceStatus, p?: UserPresence): string {
    if (status !== 'offline') {
      return p?.onlineSince ? `Checked in ${this.clock(new Date(p.onlineSince))}` : 'Online';
    }
    return p?.lastSeenAt ? `Last seen ${this.ago(new Date(p.lastSeenAt))}` : 'Not seen yet';
  }

  /** "09:12" today, "Mon 09:12" within the week, else "12 Sep". */
  private clock(d: Date): string {
    const now = new Date();
    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (d.toDateString() === now.toDateString()) return `at ${time}`;
    if (now.getTime() - d.getTime() < 6 * 86_400_000) {
      return `${d.toLocaleDateString([], { weekday: 'short' })} ${time}`;
    }
    return d.toLocaleDateString([], { day: 'numeric', month: 'short' });
  }

  private ago(d: Date): string {
    const mins = Math.max(0, Math.round((Date.now() - d.getTime()) / 60_000));
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours} h ago`;
    return this.clock(d);
  }

  private initials(name: string): string {
    const parts = name.split(/[\s@.]+/).filter(Boolean);
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
  }

  private myObjectId(): string | null {
    const account = this.msal.instance.getActiveAccount() ?? this.msal.instance.getAllAccounts()[0];
    return (account?.idTokenClaims as any)?.oid ?? account?.localAccountId ?? null;
  }
}
